import { c as _c } from "react/compiler-runtime";
import { IcoQuoteLeft24 } from '@onefootprint/icons';
import { Badge } from '@onefootprint/ui';
import { useState } from 'react';
import { AddSectionButton, AddSectionOverlay, toneStyles } from '@/components/block-primitives';
import type { QuoteBlockConfig } from './artifact-block-types';
import EditableText from './editable-text';

// Only http(s) links are turned into anchors; anything else (javascript:, data:,
// relative) is dropped so a link never becomes an injection vector.
const isHttpUrl = (value: string) => {
  try {
    const {
      protocol
    } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};
type ArtifactQuoteBlockProps = {
  config: QuoteBlockConfig;
  editable?: boolean;
  onChange?: (next: QuoteBlockConfig) => void;
};
const ArtifactQuoteBlock = t0 => {
  const $ = _c(55);
  const {
    config,
    editable: t1,
    onChange
  } = t0;
  const editable = t1 === undefined ? false : t1;
  const [addNote, setAddNote] = useState(false);
  let t2;
  if ($[0] !== config || $[1] !== onChange) {
    t2 = next => onChange?.({
      ...config,
      ...next
    });
    $[0] = config;
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const patch = t2;
  const verifiedStyles = toneStyles[config.verified ? "positive" : "warning"];
  let t3;
  if ($[3] !== config.url) {
    t3 = config.url !== undefined && isHttpUrl(config.url);
    $[3] = config.url;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const showLink = t3;
  const showNote = !!config.note || addNote;
  let t4;
  if ($[5] !== editable || $[6] !== showNote) {
    t4 = editable && !showNote && <AddSectionOverlay position="bottom"><AddSectionButton label="Add a note" onClick={() => setAddNote(true)} /></AddSectionOverlay>;
    $[5] = editable;
    $[6] = showNote;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span aria-hidden="true" className="flex"><IcoQuoteLeft24 color="tertiary" /></span>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== patch) {
    t6 = text => patch({
      text
    });
    $[9] = patch;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== config.text || $[12] !== editable || $[13] !== t6) {
    t7 = <EditableText as="div" block={true} className="mt-2 break-words text-body-1 text-primary" editable={editable} label="Quote text" multiline={true} onSubmit={t6} value={config.text} />;
    $[11] = config.text;
    $[12] = editable;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== config.verified || $[16] !== verifiedStyles) {
    t8 = config.verified !== undefined && <Badge variant={verifiedStyles.badgeVariant}>{config.verified ? "Verified" : "Unverified"}</Badge>;
    $[15] = config.verified;
    $[16] = verifiedStyles;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== patch) {
    t9 = source => patch({
      source
    });
    $[18] = patch;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== config.source || $[21] !== editable || $[22] !== t9) {
    t10 = <EditableText className="text-label-3 text-primary" editable={editable} label="Quote source" onSubmit={t9} value={config.source} />;
    $[20] = config.source;
    $[21] = editable;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== config.date) {
    t11 = config.date && <span aria-hidden="true">·</span>;
    $[24] = config.date;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== config.date || $[27] !== editable || $[28] !== patch) {
    t12 = (editable || config.date) && <EditableText editable={editable} label="Quote date" onSubmit={date => patch({
      date
    })} optional={true} placeholder="Add a date" value={config.date ?? ""} />;
    $[26] = config.date;
    $[27] = editable;
    $[28] = patch;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== config.url || $[31] !== showLink) {
    t13 = showLink && <><span aria-hidden="true">·</span><a className="text-accent hover:underline" href={config.url} target="_blank" rel="noopener noreferrer">Source</a></>;
    $[30] = config.url;
    $[31] = showLink;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t10 || $[34] !== t11 || $[35] !== t12 || $[36] !== t13 || $[37] !== t8) {
    t14 = <figcaption className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-caption-4 text-tertiary">{t8}{t10}{t11}{t12}{t13}</figcaption>;
    $[33] = t10;
    $[34] = t11;
    $[35] = t12;
    $[36] = t13;
    $[37] = t8;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t14 || $[40] !== t7) {
    t15 = <div className="px-4 py-4 md:px-6 md:py-5">{t5}{t7}{t14}</div>;
    $[39] = t14;
    $[40] = t7;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== addNote || $[43] !== config.note || $[44] !== editable || $[45] !== patch || $[46] !== showNote) {
    t16 = showNote && <div className="border-t border-tertiary bg-secondary px-4 md:px-6 py-3 text-caption-4 text-tertiary"><EditableText autoFocus={addNote} block={true} editable={editable} label="Quote note" onSubmit={note => patch({
        note
      })} onEmptyBlur={() => setAddNote(false)} optional={true} placeholder="Add a note" value={config.note ?? ""} /></div>;
    $[42] = addNote;
    $[43] = config.note;
    $[44] = editable;
    $[45] = patch;
    $[46] = showNote;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== config.source || $[49] !== t15 || $[50] !== t16) {
    t17 = <figure aria-label={config.source} className="overflow-hidden rounded-lg border border-tertiary bg-primary">{t15}{t16}</figure>;
    $[48] = config.source;
    $[49] = t15;
    $[50] = t16;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== t17 || $[53] !== t4) {
    t18 = <div className="group relative">{t4}{t17}</div>;
    $[52] = t17;
    $[53] = t4;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  return t18;
};
export default ArtifactQuoteBlock;